import React, { useEffect, useState, useRef } from 'react'
import PropTypes from 'prop-types'
import FiltersNav from './filters-nav'
import { useFirebaseService } from 'theme/services/firebase-service'
import Sessions from './sessions'
import ApiGateway from 'theme/api/api-gateway'
import { getLocalDay } from 'theme/utils/timezone-helpers'
import { useAuthService } from 'theme/services/auth/auth-service'
import { useTimezoneService } from 'theme/services/timezoneService'
import { useLocation } from '@gatsbyjs/reach-router'
import { createBrowserHistory } from 'history'
import { getFiltersFromSessions, FILTERS, prepareInitialFilters, prepareURLString } from './filters-helpers'
import { off } from 'firebase/database'
import { useApp } from 'theme/services/app-service'

const AgendaTemplate = ({
  sessions,
  post,
  format = 'conference',
  conferenceId,
  conferenceLocation,
  conferenceStartDate,
  conferenceVenueType,
  conferenceTimezone,
  filters = 'favourite day delivery format stream stage locations',
  sort,
  noTop,
}) => {
  const app = useApp() // TODO: here we have info which app is running with what global params
  const firebase = useFirebaseService()
  const user = useAuthService()?.user
  const timezoneService = useTimezoneService()
  // const conference = data.conference
  const location = useLocation()
  const history = typeof window !== 'undefined' ? createBrowserHistory() : undefined
  const currentLocationSearch = useRef(location?.search ?? '')
  // const initialSessionList = prepareInitialSessionList(sessions)
  // const sessionsFromConf = data.sessions.nodes.length > 0 ? conference : data.conference.data.Previous_Conference ? data.conference.data.Previous_Conference[0] : conference
  const [allSessionsList, setAllSessionList] = useState(sessions)
  const [sessionsList, setSessionsList] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [currentFilters, setFilters] = useState(prepareInitialFilters(location))
  const isTimezoneReadFromDB = timezoneService.isReadFromDB()

  useEffect(() => {
    if (!user) {
      return
    }

    const favouriteSessionsDBRef = ApiGateway.observeAllFavouriteSessions(firebase, user.uid, (values) => {
      const favouriteSessionsIds = values.map((item) => item.sessionId)

      setAllSessionList((prevList) => {
        return prevList.map((session) => {
          session.isFavourite = favouriteSessionsIds.includes(session.id)
          return session
        })
      })
    })

    return () => {
      off(favouriteSessionsDBRef)
    }
  }, [user])

  useEffect(() => {
    if (isTimezoneReadFromDB && sort) {
      setAllSessionList((prevList) => {
        return prevList.sort((a, b) => {
          return new Date(a.data.UTC_Start_Time).getTime() - new Date(b.data.UTC_Start_Time).getTime()
        })
      })
    }
  }, [isTimezoneReadFromDB])

  useEffect(() => {
    const notRequireFiltering =
      !currentFilters.type &&
      !currentFilters.date &&
      !currentFilters.formats.length &&
      !currentFilters.deliveries.length &&
      !currentFilters.locations.length &&
      !currentFilters.conferenceTypes.length &&
      !currentFilters.stages.length &&
      !currentFilters.streams.length
    const filteredList = notRequireFiltering
      ? allSessionsList
      : allSessionsList.filter((session) => {
          let matchFilters = true

          if (currentFilters.type) {
            if (currentFilters.type === 'favourite' && !session.isFavourite) {
              matchFilters = false
            }
          }

          if (currentFilters.date) {
            // the timezone should be the same as the conference timezone if the event is not online
            const timezone = conferenceVenueType === 'online' ? timezoneService.timezone : conferenceTimezone
            const date = getLocalDay(session.data.UTC_Start_Time, timezone)

            if (!currentFilters.date.includes(date)) {
              matchFilters = false
            }
          }

          // if (currentFilters.sessionTypes.length) {
          //   const sessionType = session.data.Session_Groups?.[0].data?.Group ?? null

          //   if (!sessionType || !(sessionType && currentFilters.sessionTypes.includes(sessionType))) {
          //     matchFilters = false
          //   }
          // }
          if (currentFilters.formats.length) {
            const format = session.data.Session_Groups?.[0].data?.Format ?? null

            if (!format || !(format && currentFilters.formats.includes(format))) {
              matchFilters = false
            }
          }
          if (currentFilters.deliveries.length) {
            const delivery = session.data.Session_Groups?.[0].data?.Delivery ?? null

            if (!delivery || !(delivery && currentFilters.deliveries.includes(delivery))) {
              matchFilters = false
            }
          }
          if (currentFilters.locations.length) {
            const locations = session.data.Session_Groups?.[0].data?.Location ?? []

            if (locations.length === 0) matchFilters = false
            else {
              let tempMatch = false
              locations.forEach((location) => {
                if (currentFilters.locations.includes(location)) {
                  tempMatch = true
                }
              })
              if (!tempMatch) matchFilters = false
            }
          }
          if (currentFilters.conferenceTypes.length) {
            const conferenceType = session.data.Conference?.[0].data?.Type ?? null

            if (
              !conferenceType ||
              !(conferenceType && currentFilters.conferenceTypes.includes(conferenceType))
            ) {
              matchFilters = false
            }
          }

          if (currentFilters.streams.length) {
            const stream = session.data.Stream?.[0].data?.Stream_Name ?? null

            if (!stream || !(stream && currentFilters.streams.includes(stream))) {
              matchFilters = false
            }
          }

          if (currentFilters.stages.length) {
            const stage = session.data.Stage?.[0].data?.Stage_Name ?? null

            if (!stage || !(stage && currentFilters.stages.includes(stage))) {
              matchFilters = false
            }
          }

          if (matchFilters) {
            return session
          }
        })

    setSessionsList(filteredList)
    if (filteredList) setIsLoading(false)
    const queryString = prepareURLString(currentFilters, location, history)

    if (queryString !== currentLocationSearch.current) {
      currentLocationSearch.current = queryString

      if (location) {
        if (location.pathname.substr(-1) === '/') {
          location.pathname = location.pathname.slice(0, -1)
        }

        if (history) {
          history.push(`${location.pathname}${`?${queryString}`}`)
        }
      }
    }
  }, [currentFilters, allSessionsList, timezoneService.timezone])

  const changeFilters = (type, value) => {
    switch (type) {
      case FILTERS.TYPE:
        setFilters((prevFilters) => {
          return {
            ...prevFilters,
            type: value,
          }
        })
        break
      case FILTERS.DAY:
        setFilters((prevFilters) => {
          return {
            ...prevFilters,
            date: value,
          }
        })
        break
      // case FILTERS.SESSION_TYPE:
      //   setFilters((prevFilters) => {
      //     return {
      //       ...prevFilters,
      //       sessionTypes: value
      //     }
      //   })
      //   break
      case FILTERS.CONFERENCE_TYPE:
        setFilters((prevFilters) => {
          return {
            ...prevFilters,
            conferenceTypes: value,
          }
        })
        break
      case FILTERS.FORMAT:
        setFilters((prevFilters) => {
          return {
            ...prevFilters,
            formats: value,
          }
        })
        break
      // case FILTERS.DELIVERY:
      //   setFilters((prevFilters) => {
      //     return {
      //       ...prevFilters,
      //       deliveries: value,
      //     }
      //   })
      //   break
      case FILTERS.LOCATIONS:
        setFilters((prevFilters) => {
          return {
            ...prevFilters,
            locations: value,
          }
        })
        break
      case FILTERS.STREAM:
        setFilters((prevFilters) => {
          return {
            ...prevFilters,
            streams: value,
          }
        })
        break
      case FILTERS.STAGE:
        setFilters((prevFilters) => {
          return {
            ...prevFilters,
            stages: value,
          }
        })
        break
    }
  }

  return (
    <>
      <FiltersNav
        allFilters={getFiltersFromSessions(allSessionsList, timezoneService.timezone, conferenceTimezone)}
        currentFilters={currentFilters}
        changeFilters={changeFilters}
        conferenceLocation={conferenceLocation}
        conferenceTimezone={conferenceTimezone}
        filters={filters}
        noTop={noTop}
      />

      {isLoading && (
        <div className="mx-auto w-32 pt-8 flex items-center">
          <div>
            <svg
              className="animate-spin -ml-1 mr-3 h-12 w-12 text-black"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          </div>
        </div>
      )}
      {!isLoading && (
        <Sessions
          sessionsList={sessionsList}
          timezone={timezoneService.timezone}
          conferenceId={conferenceId}
          post={post}
          format={format}
          conferenceLocation={conferenceLocation}
          conferenceStartDate={conferenceStartDate}
          conferenceVenueType={conferenceVenueType}
          conferenceTimezone={conferenceTimezone}
        />
      )}
    </>
  )
}

AgendaTemplate.propTypes = {
  sessions: PropTypes.array.isRequired,
  post: PropTypes.bool,
  format: PropTypes.string,
  conferenceId: PropTypes.string,
  conferenceLocation: PropTypes.string,
  filters: PropTypes.string,
  sort: PropTypes.bool,
  noTop: PropTypes.bool,
  conferenceStartDate: PropTypes.string.isRequired,
  conferenceVenueType: PropTypes.array.isRequired,
}

export default AgendaTemplate
